<script>
import { goToLogin } from '@/utils/loginFlow'
import { tryClaimPendingGuestExam } from '@/utils/examClaim'
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import ImageBar from './components/ImageBar.vue'
import MarkdownBody from './components/MarkdownBody.vue'
import ExamPromoBadge from './components/exam/ExamPromoBadge.vue'
import {
  isExamFree,
  isExamChargeable,
  isExamAcquired,
  isLimitedTimeFree,
  isLimitedTimePromo,
  isPermanentFreeExam,
  getExamPriceText,
  formatExamFeeYuan,
  formatExamOriginFeeYuan,
  examFeeCents,
  normalizeExamFromApi,
  getExamRouteKey,
  examTakePath,
  examResultListPath
} from '@/utils/examPricing'

export default {
  name: 'ExamDetailView',
  components: { ImageBar, MarkdownBody, ExamPromoBadge },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const exam = ref(null)
    const loading = ref(false)
    const buying = ref(false)
    const examId = ref(route.params.id)

    const isLoggedIn = computed(() => !!localStorage.getItem('jwt_token'))

    const fetchExam = async () => {
      try {
        loading.value = true
        const token = localStorage.getItem('jwt_token')
        const seg = encodeURIComponent(String(examId.value))
        const url = token ? `/api/v1/exam/${seg}` : `/api/v1/exam/guest/${seg}`
        const headers = { 'Content-Type': 'application/json' }
        if (token) {
          headers.Authorization = `Bearer ${token}`
        }
        const response = await axios.get(url, { headers })
        if (response.data.code === 200) {
          exam.value = normalizeExamFromApi(response.data.data)
          const key = getExamRouteKey(exam.value)
          if (key && String(route.params.id) !== String(key)) {
            router.replace({ name: 'ExamDetail', params: { id: key } })
          }
          examId.value = key
          if (token && isExamAcquired(exam.value)) {
            await tryClaimPendingGuestExam(router, exam.value.id)
          }
        } else {
          ElMessage.error(response.data.message || '获取测评详情失败')
        }
      } catch (error) {
        console.error('Error fetching exam:', error)
        ElMessage.error('获取测评详情失败')
      } finally {
        loading.value = false
      }
    }

    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      return new Date(timestamp * 1000).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const startExam = async () => {
      const token = localStorage.getItem('jwt_token')
      if (!token) {
        router.push(examTakePath(exam.value))
        return
      }

      if (isExamChargeable(exam.value) && !isExamAcquired(exam.value)) {
        try {
          await ElMessageBox.confirm(
            `确定要购买此测评吗？费用：¥${formatExamFeeYuan(exam.value)}`,
            '购买确认',
            {
              confirmButtonText: '确定购买',
              cancelButtonText: '取消',
              type: 'info'
            }
          )
        } catch {
          return
        }

        try {
          buying.value = true
          const balanceResponse = await axios.get('/api/v1/user/balance', {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })

          if (balanceResponse.data.code === 200 && balanceResponse.data.data?.length > 0) {
            const userBalance = balanceResponse.data.data[0].balance
            const examFee = examFeeCents(exam.value)
            if (userBalance < examFee) {
              ElMessage.error(
                `余额不足，需要 ¥${(examFee / 100).toFixed(2)}，当前余额 ¥${(userBalance / 100).toFixed(2)}`
              )
              router.push('/recharge')
              return
            }
          }

          const response = await axios.post(
            '/api/v1/exam/buy',
            { id: exam.value.id },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          )

          if (response.data.code === 200) {
            exam.value.isAcquired = 1
            const resultId = await tryClaimPendingGuestExam(router, exam.value.id)
            if (resultId) {
              return
            }
            ElMessage.success('购买成功，开始测评')
            router.push(examTakePath(exam.value))
          } else {
            ElMessage.error(response.data.message || '购买失败')
          }
        } catch (error) {
          console.error('Error buying exam:', error)
          if (error.response?.status === 401) {
            goToLogin(router)
          } else if (error.response?.status === 402) {
            ElMessage.error('余额不足，请先充值')
            router.push('/recharge')
          } else {
            ElMessage.error('购买失败，请重试')
          }
        } finally {
          buying.value = false
        }
      } else {
        router.push(examTakePath(exam.value))
      }
    }

    const viewHistory = () => {
      router.push(examResultListPath(exam.value))
    }

    onMounted(fetchExam)

    return {
      exam,
      loading,
      buying,
      formatDate,
      startExam,
      viewHistory,
      isLoggedIn,
      isExamFree,
      isExamAcquired,
      isLimitedTimeFree,
      isLimitedTimePromo,
      isPermanentFreeExam,
      getExamPriceText,
      formatExamFeeYuan,
      formatExamOriginFeeYuan
    }
  }
}
</script>

<template>
  <ImageBar class="exam-detail-banner" />
  <div class="exam-detail-container exam-detail-page" v-loading="loading">
    <template v-if="exam">
      <div class="exam-content">
        <div class="exam-header">
          <h1>{{ exam.examName }}</h1>
          <div class="exam-meta">
            <span class="exam-meta-item">
              <span class="date">创建时间：{{ formatDate(exam.createdAt) }}</span>
            </span>
            <span class="exam-meta-item">
              <span class="question-count">题目数量：{{ exam.questionCount }}</span>
            </span>
            <span v-if="isPermanentFreeExam(exam)" class="exam-meta-item price-promo">
              <ExamPromoBadge :exam="exam" variant="inline" />
            </span>
            <span v-else-if="isLimitedTimeFree(exam)" class="exam-meta-item price-promo">
              <span class="price-original">原价 ¥{{ formatExamOriginFeeYuan(exam) }}</span>
              <ExamPromoBadge :exam="exam" variant="inline" />
            </span>
            <span v-else-if="isLimitedTimePromo(exam)" class="exam-meta-item price-promo">
              <span class="price-sale">¥{{ formatExamFeeYuan(exam) }}</span>
              <span class="price-original">原价 ¥{{ formatExamOriginFeeYuan(exam) }}</span>
              <ExamPromoBadge :exam="exam" variant="inline" />
            </span>
            <span v-else-if="isExamFree(exam)" class="exam-meta-item price-free">免费</span>
            <span v-else-if="!isExamAcquired(exam)" class="exam-meta-item price-paid">
              ¥{{ formatExamFeeYuan(exam) }}
            </span>
          </div>
          <div class="exam-actions">
            <el-button type="primary" @click="startExam" :loading="buying">
              {{ isExamFree(exam) || isExamAcquired(exam) ? '开始测评' : '购买并开始测评' }}
            </el-button>
            <el-button v-if="isLoggedIn" type="info" @click="viewHistory">测试历史</el-button>
          </div>
        </div>

        <div v-if="exam.cover" class="cover-banner">
          <img :src="exam.cover" alt="封面" class="cover" />
          <ExamPromoBadge :exam="exam" variant="cover" />
        </div>

        <div class="exam-info">
          <h2>测评简介</h2>
          <p class="profile">{{ exam.profile }}</p>

          <h2>详细说明</h2>
          <MarkdownBody :content="exam.description" empty-text="暂无详细说明" exam-preprocess />
        </div>
      </div>
    </template>

    <div v-else-if="!loading" class="empty-state">
      <el-empty description="未找到测评信息" />
    </div>
  </div>
</template>

<style scoped>
.exam-detail-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
}

.exam-content {
  background-color: #fff;
}

.exam-header {
  margin-bottom: 30px;
}

.exam-header h1 {
  margin: 0 0 20px;
  font-size: 32px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  line-height: 1.4;
  word-break: break-word;
}

.exam-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  color: var(--el-text-color-secondary);
  font-size: 14px;
}

.exam-meta-item {
  display: inline-flex;
  align-items: center;
}

.exam-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}

.price-promo {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.price-original {
  text-decoration: line-through;
  color: #909399;
}

.price-sale {
  color: #e6a23c;
  font-weight: 600;
}

.price-free {
  color: #67c23a;
  font-weight: 500;
}

.price-paid {
  color: #e6a23c;
  font-weight: 500;
}

.cover-banner {
  margin-bottom: 20px;
  position: relative;
  width: 100%;
  background: var(--el-fill-color-light);
  border-radius: 8px;
  overflow: hidden;
}

.cover {
  width: 100%;
  max-height: 320px;
  object-fit: cover;
  display: block;
  vertical-align: middle;
}

.exam-info {
  margin-bottom: 30px;
}

.exam-info h2 {
  margin: 0 0 12px;
  font-size: 20px;
  color: var(--el-text-color-primary);
}

.exam-info h2:not(:first-child) {
  margin-top: 28px;
}

.profile {
  margin: 0;
  line-height: 1.7;
  color: var(--el-text-color-regular);
  white-space: pre-wrap;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

@media (max-width: 768px) {
  .exam-detail-container {
    padding: 10px;
  }

  .exam-content {
    padding: 10px;
  }

  .exam-header h1 {
    font-size: 24px;
  }

  .exam-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .exam-actions {
    margin-top: 16px;
    width: 100%;
  }

  .exam-actions .el-button {
    flex: 1 1 auto;
  }

  .cover {
    max-height: 200px;
  }
}
</style>
